import {
  ControlledDateInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { Checkbox, TextInput } from "components/inputs";
import DateOfBirthInput from "components/inputs/DateOfBirthInput/DateOfBirthInput";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { GENDERS, PLAN_INSURANCE_TYPES } from "constants/FormConstants";
import { format } from "date-fns";
import type {
  Control,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { useWatch } from "react-hook-form";
import type { ConstantTypes } from "types/DataTypes";

import SectionBreak from "components/SectionBreak";
import type { LienFormValues } from "../LienTypes";
import { TPOCSection } from "./LienTPOCSection";

type LienOverviewSectionProps = {
  control: Control<LienFormValues>;
  getValues: UseFormGetValues<LienFormValues>;
  setValue: UseFormSetValue<LienFormValues>;
  handleFieldChange: (field: string | null, value: unknown) => Promise<void>;
  constants?: ConstantTypes;
};

const LienOverviewSection = ({
  control,
  getValues,
  setValue,
  handleFieldChange,
  constants,
}: LienOverviewSectionProps) => {
  const birthDateString = useWatch({ control, name: "birth_date" });
  const birthDate = birthDateString ? new Date(birthDateString) : null;

  const ormValue = getValues("orm_indicator") ?? false;
  const isOrmEnabled =
    typeof ormValue === "object"
      ? // @ts-expect-error - handle select option object
        ormValue?.value === "true"
      : ormValue === "true";

  const insuranceTypeValue = getValues("insurance_type") ?? "";
  const showNoFaultFields =
    typeof insuranceTypeValue === "object"
      ? // @ts-expect-error - handle select option object
        insuranceTypeValue?.value === "no_fault"
      : insuranceTypeValue === "no_fault";

  return (
    <div className="swim-column">
      <FutureMedicalCard heading="Overview Information">
        <ControlledSelect
          control={control}
          name="insurance_type"
          label="Insurance Type"
          errors={{}}
          onSave={(option) =>
            // clear the no fault exhaust date if the insurance type is not no fault
            handleFieldChange(null, {
              insurance_type: option?.value,
              no_fault_exhaust_date:
                option?.value !== "no_fault"
                  ? null
                  : getValues("no_fault_exhaust_date"),
            })
          }
          options={PLAN_INSURANCE_TYPES}
        />

        {showNoFaultFields && (
          <ControlledDateInput
            control={control}
            name="no_fault_exhaust_date"
            label="No Fault Exhaust Date"
            errors={{}}
            onSave={(value) =>
              handleFieldChange("no_fault_exhaust_date", value)
            }
            allowFutureDates={true}
          />
        )}

        <ControlledSelect
          control={control}
          name="orm_indicator"
          label="ORM Indicator"
          errors={{}}
          onSave={async (option) => {
            // if ORM is set to No, clear termination date
            if (option?.value === "false") {
              setValue("orm_termination_date", null);
              await handleFieldChange(null, {
                orm_indicator: false,
                orm_termination_date: null,
              });
            } else {
              await handleFieldChange(
                "orm_indicator",
                option?.value === "true",
              );
            }
          }}
          options={[
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]}
        />

        {isOrmEnabled && (
          <ControlledDateInput
            control={control}
            name="orm_termination_date"
            label="ORM Termination Date"
            errors={{}}
            allowFutureDates={true}
            onSave={(value) => handleFieldChange("orm_termination_date", value)}
          />
        )}

        {/* TPOC Section - now always visible */}
        <TPOCSection
          // @ts-expect-error - need to massage the control to be the correct type
          control={control}
          // @ts-expect-error - need to massage
          getValues={getValues}
          onSave={handleFieldChange}
        />

        <SectionBreak />

        <DateOfBirthInput
          name="birth_date"
          label="Date of Birth"
          initialDate={birthDate}
          onDateChange={async (date) => {
            const dateString = date ? format(date, "yyyy-MM-dd") : "";
            setValue("birth_date", dateString);
            await handleFieldChange("birth_date", dateString);
          }}
          errors={{}}
        />

        <ControlledInput
          control={control}
          name="first_name"
          label="First Name"
          errors={{}}
          onSave={(value) => handleFieldChange("first_name", value)}
          componentType={TextInput}
        />

        <ControlledInput
          control={control}
          name="middle_initial"
          label="Middle Initial"
          errors={{}}
          onSave={(value) => handleFieldChange("middle_initial", value)}
          componentType={TextInput}
          maxLength={1}
        />

        <ControlledInput
          control={control}
          name="last_name"
          label="Last Name"
          errors={{}}
          onSave={(value) => handleFieldChange("last_name", value)}
          componentType={TextInput}
        />

        <ControlledSelect
          control={control}
          name="gender"
          label="Gender"
          errors={{}}
          options={GENDERS}
          onSave={(option) => handleFieldChange("gender", option?.value)}
        />

        <ControlledInput
          control={control}
          name="address"
          label="Address"
          errors={{}}
          onSave={(value) => handleFieldChange("address", value)}
          componentType={TextInput}
        />

        <ControlledInput
          control={control}
          name="city"
          label="City"
          errors={{}}
          onSave={(value) => handleFieldChange("city", value)}
          componentType={TextInput}
        />

        <ControlledSelect
          control={control}
          name="state"
          label="State"
          errors={{}}
          options={constants?.STATES}
          onSave={(option) => handleFieldChange("state", option?.value)}
        />

        <ControlledInput
          control={control}
          name="zip_code"
          label="Zip"
          errors={{}}
          onSave={(value) => handleFieldChange("zip_code", value)}
          componentType={TextInput}
        />

        <Checkbox
          name="is_represented"
          label="Is Represented"
          checked={getValues("is_represented")}
          required={false}
          onChange={async (e) => {
            setValue("is_represented", e.target.checked);
            await handleFieldChange("is_represented", e.target.checked);
          }}
        />

        <Checkbox
          name="reasonable_expectation"
          label="Reasonable Expectation"
          checked={getValues("reasonable_expectation")}
          required={false}
          onChange={async (e) => {
            setValue("reasonable_expectation", e.target.checked);
            await handleFieldChange("reasonable_expectation", e.target.checked);
          }}
        />

        <ControlledInput
          control={control}
          name="defense_counsel"
          label="Defense Counsel"
          errors={{}}
          onSave={(value) => handleFieldChange("defense_counsel", value)}
          componentType={TextInput}
        />
      </FutureMedicalCard>
    </div>
  );
};

export default LienOverviewSection;
